import {EmployeeEventResponseDto} from "../../../models/event/employee-event-response-dto";

/**
 * The repo-standard structured backend error (`CheqMe.Errors.ApiError`) as it appears on the wire.
 *
 * The web app has no shared declaration for it yet — every call site spells the shape inline — so it is
 * declared here for the shift-maker payloads. **Branch on {@link code}, never render {@link description}
 * to the user** — despite the field's own doc, it is NOT safe to show: `ErrorCatalog` descriptions are
 * developer-facing English by design (Postman, logs, docs), and `ShiftMakerComponent` used to render it
 * straight into an otherwise fully German UI (the #386/#392 codes shipped English into the "Fehler" banner
 * from day one). `ShiftMakerComponent.messageFor` is the code → German mapping; extend it, don't reach for
 * `description`.
 */
export interface ShiftMakerApiError {
  /** Stable machine-readable code, e.g. `E_SHIFTMAKER_EMPLOYEE_IMMUTABLE`. */
  code: string;
  /**
   * Developer-facing English description from the server's `ErrorCatalog` — Postman/logs/docs only.
   * **Never render this to the user**; see the interface doc above.
   */
  description: string;
  /** HTTP status the code maps to. */
  statusCode: number;
}

/**
 * The reply `ShiftMakerHub` sends back for a single availability push.
 *
 * Mirrors `API.Core/Models/Company/ShiftMakerResponse.cs`; see
 * `docs/investigations/386-hub-contract.md` for the authoritative wire contract.
 */
export class ShiftMakerResponse {
  /** Whether the push was accepted and persisted. */
  success: boolean = false;
  /**
   * The server's authoritative id for the row this push referred to — the newly minted id when the push
   * created the row, the row's own id when it updated one, the echoed client id on a refusal, and `""` when
   * nothing was created. Adopted by `ShiftMakerComponent.SendChange` (Bug #386).
   */
  nextId: string = "";
  /** HTTP-style status of the outcome. */
  code: number = 0;
  /** Human-readable description of the outcome; empty on success. */
  message: string = "";
  /** The persisted state of the touched availability, or absent on a refusal. */
  employeeEventResponse?: EmployeeEventResponseDto;
  /**
   * The structured refusal, or absent on success. Branch on `error.code` (see {@link ShiftMakerApiError} —
   * `error.description` is server-side English, never shown to the user). Added by #386/#392, when the hub
   * stopped throwing at its clients.
   */
  error?: ShiftMakerApiError;
}

/**
 * The reply to a batched re-index (`PushGeneralIndexUpdates` / `PushLiveIndexUpdates`).
 *
 * One item failing never aborts the batch: {@link results} always carries one entry per submitted item, at
 * the same position, so the caller can reconcile card by card.
 */
export class ShiftMakerBatchResponse {
  /** Whether **every** submitted item was accepted. */
  success: boolean = false;
  /** HTTP-style status: 200 when everything was accepted, otherwise the first refusal's status. */
  code: number = 0;
  /** Human-readable description of the outcome; empty when everything was accepted. */
  message: string = "";
  /** The structured refusal when the batch envelope itself was rejected (empty or oversized). */
  error?: ShiftMakerApiError;
  /** One outcome per submitted item, in submission order. */
  results: ShiftMakerResponse[] = [];
}
